import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { useCompare } from "@/context/CompareContext";
import api from "@/lib/api";
import { formatPrice } from "@/components/PropertyCard";
import { X, Plus } from "lucide-react";

const CompareCell = ({ label, value }) => (
  <div className="border-b border-slate-100 py-4">
    <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-1">{label}</div>
    <div className="text-slate-800 text-sm">{value || "—"}</div>
  </div>
);

export default function Compare() {
  const { items, remove } = useCompare();
  const [stats, setStats] = useState([]);
  const [allProjects, setAllProjects] = useState([]);

  useEffect(() => {
    api.get("/projects/cities/stats").then(r => setStats(r.data || []));
    if (items.length === 0) api.get("/projects?limit=12").then(r => setAllProjects(r.data || []));
  }, [items.length]);

  return (
    <div data-testid="compare-page" className="min-h-screen pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-12">
        <div className="overline mb-3">Decision Tools</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-light text-slate-900">Price Comparison</h1>
        <p className="text-slate-600 mt-3 max-w-2xl font-light">Pit up to three projects side-by-side and benchmark them against city averages.</p>
      </div>

      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-16">
        {items.length === 0 ? (
          <div className="border border-slate-200 p-12">
            <h3 className="font-serif text-2xl text-slate-900 mb-3">No projects added yet</h3>
            <p className="text-slate-600 mb-6">Browse projects and click "Add to Compare" to start.</p>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {allProjects.slice(0, 6).map((p) => (
                <Link key={p.id} to={`/projects/${p.id}`} className="border border-slate-200 p-4 hover:border-amber-200 group">
                  <div className="overline mb-1">{p.city}</div>
                  <div className="font-serif text-lg text-slate-900 group-hover:text-[#D4A437]">{p.name}</div>
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="grid border border-slate-200" style={{ gridTemplateColumns: `200px repeat(${items.length}, 1fr)` }}>
              <div className="bg-amber-50/40 border-r border-slate-100 p-6 sticky left-0">
                <div className="overline">Comparing</div>
                <div className="font-serif text-2xl mt-2 text-slate-900">{items.length} project{items.length > 1 ? "s" : ""}</div>
              </div>
              {items.map((p) => (
                <div key={p.id} className="p-6 border-r border-slate-100 last:border-r-0 relative">
                  <button onClick={() => remove(p.id)} data-testid={`remove-compare-${p.id}`} className="absolute top-3 right-3 text-slate-500 hover:text-[#D4A437]">
                    <X className="w-4 h-4" />
                  </button>
                  <div className="aspect-[16/10] bg-slate-50 mb-4 overflow-hidden">
                    <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="overline mb-1">{p.city}</div>
                  <Link to={`/projects/${p.id}`} className="font-serif text-xl text-slate-900 hover:text-[#D4A437] block">{p.name}</Link>
                  <div className="text-xs text-slate-500 mt-1">{p.developer}</div>
                </div>
              ))}

              {/* Rows */}
              {[
                { label: "Starting Price", get: (p) => formatPrice(p.price_min, p.currency) },
                { label: "Max Price", get: (p) => formatPrice(p.price_max, p.currency) },
                { label: "Per Sqft", get: (p) => `${p.price_per_sqft?.toLocaleString()} ${p.currency}` },
                { label: "BHK", get: (p) => p.bhk?.join(", ") },
                { label: "Area (sqft)", get: (p) => `${p.area_min}–${p.area_max}` },
                { label: "Type", get: (p) => p.type },
                { label: "Status", get: (p) => p.status?.replace("_", " ") },
                { label: "Possession", get: (p) => p.possession || "Ready" },
                { label: "Locality", get: (p) => p.locality },
                { label: "RERA", get: (p) => p.rera || "Verified" },
                { label: "Top Amenities", get: (p) => (p.amenities || []).slice(0, 4).join(" · ") },
              ].map((row, idx) => (
                <React.Fragment key={row.label}>
                  <div className={`p-4 bg-amber-50/40 border-r border-slate-100 sticky left-0 border-t border-slate-100`}>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500">{row.label}</div>
                  </div>
                  {items.map((p) => (
                    <div key={p.id} className="p-4 text-sm text-slate-700 border-t border-slate-100 border-r border-slate-100 last:border-r-0">
                      {row.get(p) || "—"}
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* City stats chart */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-24">
        <div className="overline mb-3">Market Snapshot</div>
        <h2 className="font-serif text-3xl sm:text-4xl text-slate-900 font-light">Average price per sqft, by city</h2>
        <div className="border border-slate-200 p-6 mt-8">
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats} margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="city" stroke="#64748b" tick={{ fill: "#94a3b8", fontSize: 12 }} />
                <YAxis stroke="#64748b" tick={{ fill: "#94a3b8", fontSize: 12 }} />
                <Tooltip
                  contentStyle={{ background: "#020617", border: "1px solid #334155", color: "#f8fafc" }}
                  cursor={{ fill: "#0f172a" }}
                />
                <Bar dataKey="avg_price_per_sqft" fill="#d97706" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="text-xs text-slate-500 mt-4">Note: Prices in local currency. INR for Indian cities; AED for Dubai; SGD for Singapore.</div>
        </div>
      </section>
    </div>
  );
}
